<template>
  <tr :class="{ 'not-viewed': row.view_count == 0 }">
    <td v-for="(column, r) in columns" :key="r">
      <!-- Alert type -->
      <span
        v-if="
          viewedByPatient &&
            column.name === 'passport' &&
            tableType !== 'pathology'
        "
        class="table-label"
        :style="viewedByPatientPassportLabelStyle"
      >
        מטופל צפה
      </span>

      <span
        v-if="alertType && column.name === 'passport'"
        class="table-label"
        :style="alertPassportLabelStyle"
      >
        {{ row.alert_type }}
      </span>

      <!-- 'Scheduled by' icon -->
      <slot v-if="column.name === 'scheduled_by'">
        <span>{{ getAppScheduledByText(row[column.name]) }}</span>
      </slot>

      <!-- Surgery name -->
      <slot v-else-if="column.name === 'title'">
        <span class="td-surgery-name" @click="openEventPage">
          {{ row[column.name] }}
        </span>
      </slot>

      <slot v-else-if="column.name === 'documents_count'">
        <div class="relative">
          <template v-if="isPathology && row.path">
            <div class="docs-table flex">
              <div
                v-for="(doc, d) in pathologyDocumentBlocks"
                :key="d"
                role="button"
                :tabindex="doc.disabled ? -1 : 0"
                class="doc-table-container relative pathology-doc-hit"
                :class="pathologyDocBlockClass(doc)"
                @click.stop="runPathologyDocumentAction(doc)"
                @keydown="handlePathologyDocsKeydown($event, doc)"
              >
                <div class="doc-table-label" style="font-size: 16px;">
                  {{ doc.label }}
                </div>
                {{ doc.cta }}
              </div>
            </div>
          </template>
          <template v-else-if="isPathology">
            <div v-if="row[column.name] === '-'" @click="openEventPage">
              -
            </div>
            <button class="view-files-btn" v-else @click.stop="openFilesPopup">
              <span class="flex">
                <img src="/assets/img/svg/ic-file-blue.svg" />
                פתיחת המסמך
              </span>
            </button>
          </template>
          <template v-else>
            <div v-if="row[column.name] === '-'" @click="openEventPage">
              -
            </div>
            <button class="view-files-btn" v-else @click.stop="openFilesPopup">
              <span class="flex">
                <img src="/assets/img/svg/ic-file-blue.svg" />
                הצגת מסמכים
              </span>
            </button>
          </template>
        </div>
      </slot>

      <slot v-else-if="column.name === 'patient_phone'">
        <a
          class="phone-text"
          :href="`tel:${row[column.name]}`"
          @click.stop="null"
          style="font-size: 1rem;"
        >
          {{ row[column.name] }}
          <img src="/assets/img/svg/ic-phone.svg" />
        </a>

        <a class="phone-text" @click.stop="openWhatsapp(row[column.name])">
          <img src="/assets/img/svg/wts.svg" alt="" />
        </a>
      </slot>

      <slot v-else-if="column.name === 'patient_view_status'">
        <div class="docs-table flex">
          <div
            v-for="(cell, c) in patientViewStatusCells"
            :key="c"
            class="doc-table-container relative"
          >
            <div class="doc-table-label" :style="cell.labelStyle">
              {{ cell.badgeText }}
            </div>
            <div>{{ cell.caption }}</div>
          </div>
        </div>
      </slot>

      <!-- Is available online -->
      <slot v-else-if="column.name === 'is_available_online' && isPathology">
        <div class="docs-table flex">
          <div
            class="doc-table-container"
            :class="{
              available: row.is_available_online === 'זמין',
              unavailable: row.is_available_online === 'לא זמין'
            }"
          >
            <div class="doc-table-label">דוח פתולוגיה</div>
            {{ row.is_available_online }}
          </div>
          <div
            class="doc-table-container availability-change-btn"
            :class="[
              'pathology-availability-cta',
              { disabled: row.is_available_online !== 'זמין' }
            ]"
            @click.stop="toggleAvailability"
          >
            {{ resendButtonText }}
          </div>
        </div>
      </slot>

      <!-- IVF files -->
      <slot
        v-else-if="
          column.name === 'existing_files' || column.name === 'missing_files'
        "
      >
        <div class="relative">
          <div v-if="!row[column.name]?.length">
            -
          </div>
          <button
            class="view-files-btn"
            v-else
            @click.stop="openFilesPopupIVF(row[column.name])"
          >
            <span class="flex">
              <img src="/assets/img/svg/ic-file-blue.svg" />
              הצגת מסמכים
            </span>
          </button>
        </div>
      </slot>

      <!-- IVF button -->
      <slot v-else-if="column.name === 'ivf_button'">
        <div class="relative">
          <button class="view-files-btn ivf-btn" @click.stop="sendCellosign">
            שליחת טפסי הסכמה
          </button>
        </div>
      </slot>

      <!-- Regular cells -->
      <slot v-else :name="column.name" :value="row[column.name]">
        <div
          @click="openEventPage"
          :style="
            column.name === 'passport' && viewedByPatient && alertType
              ? 'padding-right: 35px;'
              : ''
          "
        >
          {{ row[column.name] }}
        </div>
      </slot>
    </td>
  </tr>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import Error from "../Popups/Error.vue";
import Files from "../Popups/Files.vue";
import { downloadDocument } from "../../utilities/download-document.js";
import Whatsapp from "../Popups/Whatsapp.vue";
import IVF from "../Popups/IVF.vue";

const GREEN_VIEWED = "background-color: #01a87a;color: white;";

export default {
  props: {
    row: { type: Object, required: true },
    columns: { type: Array, required: true },
    tableType: { required: true }
  },
  data() {
    return {
      files: [],
      isUpdatingAvailability: false,
      wasSent: false
    };
  },
  computed: {
    ...mapGetters(["activeDoctor", "pendingSummaryIds"]),
    isPathology() {
      return this.tableType === "pathology";
    },
    eventRouteName() {
      if (!this.row) return "surg-info";
      if (this.row.pathologyType) {
        return this.row.pathologyType == "appointment"
          ? "app-info"
          : "surg-info";
      }
      return this.tableType == "appointment" ? "app-info" : "surg-info";
    },
    /** Row-level styles for the "מטופל צפה" label (passport column, non-pathology). */
    viewedByPatientPassportLabelStyle() {
      return this.alertType
        ? "background-color: #01a87a;top:24px;"
        : "background-color: #01a87a;";
    },
    alertPassportLabelStyle() {
      return this.row && this.row.alert_type != "בהול"
        ? "background-color: #f67e3e;"
        : "";
    },
    /**
     * Config for pathology document buttons when `row.path` is set.
     * Keeps a single click/keydown path instead of three near-duplicate divs.
     */
    pathologyDocumentBlocks() {
      if (!this.isPathology || !this.row || !this.row.path) return [];
      if (this.row.has_pathology_summary) {
        return [
          {
            label: "דוח פתולוגיה",
            cta: "צפייה",
            available: true,
            keyAction: "report",
            action: "openFilesPopup"
          },
          {
            label: "סיכום תוצאות",
            cta: "צפייה",
            available: true,
            keyAction: "summary",
            action: "openPathologySummary"
          }
        ];
      }
      const summaryBlock = this.pendingSummaryIds.includes(this.row.id)
        ? {
            label: "סיכום תוצאות",
            cta: "בתהליך...",
            keyAction: "create",
            disabled: true
          }
        : {
            label: "סיכום תוצאות",
            cta: "צור",
            keyAction: "create",
            action: "onPathologySummaryCreate"
          };

      return [
        {
          label: "דוח פתולוגיה",
          cta: "צפייה",
          available: true,
          keyAction: "report",
          action: "openFilesPopup"
        },
        summaryBlock
      ];
    },
    /** Two side-by-side status cells for the patient view column. */
    patientViewStatusCells() {
      return [
        {
          caption: "דוח פתולוגיה",
          badgeText: this.viewedByPatient ? "צפה" : "לא צפה",
          labelStyle: this.viewedByPatient ? GREEN_VIEWED : ""
        },
        {
          caption: "סיכום תוצאות",
          badgeText: this.row.located_report,
          labelStyle: this.row.located_report === "צפה" ? GREEN_VIEWED : ""
        }
      ];
    },
    viewedByPatient() {
      return (
        this.row && this.row.viewed_by_patient && this.row.viewed_by_patient > 0
      );
    },
    alertType() {
      return this.row && this.row.alert_type && this.row.alert_type !== "רגיל";
    },
    resendButtonText() {
      if (!this.row) return "שלח מחדש";
      if (this.isUpdatingAvailability) return "שולח...";
      if (this.wasSent) return "נשלח";
      return "שלח מחדש";
    }
  },
  methods: {
    ...mapMutations(["UPDATE_FILE_WATCHED", "ADD_PENDING_SUMMARY"]),
    showErrorModal(title) {
      this.$modal.show(Error, { title });
    },
    runPathologyDocumentAction(doc) {
      if (doc.disabled) return;
      const fn = this[doc.action];
      if (typeof fn === "function") fn.call(this);
    },
    /**
     * "צור" is an action, not a missing state — avoid global `.unavailable` (red) styling.
     */
    pathologyDocBlockClass(doc) {
      if (doc.disabled) return "pathology-doc-pending";
      if (doc.keyAction === "create") return "pathology-doc-create";
      return doc.available ? "available" : "unavailable";
    },
    onPathologySummaryCreate() {
      if (!this.row.id) return;
      this.ADD_PENDING_SUMMARY(this.row.id);
      this.$router.push({
        name: "cellosign-iframe",
        params: { type: "pathology-summary", id: this.row.id },
        query: { type: this.row.pathologyType || "surgery" }
      });
    },
    handlePathologyDocsKeydown(e, doc) {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      e.stopPropagation();
      if (doc.disabled) return;
      if (doc.keyAction === "report") this.openFilesPopup();
      else if (doc.keyAction === "summary") this.openPathologySummary();
      else this.onPathologySummaryCreate();
    },
    /** Downloads the linked pathology results summary file when available. */
    async openPathologySummary() {
      if (!this.row.pathology_summary_path) return;
      try {
        await downloadDocument(
          this.row.pathology_summary_path,
          this.row.pathology_summary_file
        );
      } catch (error) {
        this.showErrorModal("התרחשה שגיאה, אנא נסו שנית מאוחר יותר");
      }
    },
    async openFilesPopup() {
      if (this.row.path) {
        try {
          await downloadDocument(this.row.path, this.row.file);
          this.UPDATE_FILE_WATCHED(this.row.path);
        } catch (error) {
          this.showErrorModal("התרחשה שגיאה, אנא נסו שנית מאוחר יותר");
        }
        return;
      }
      this.$modal.show(Files, {
        tableType: this.tableType,
        eventId: this.row.id
      });
    },
    /**
     * Opens files popup for IVF.
     * @param {Array} files
     */
    openFilesPopupIVF(files) {
      this.$modal.show(Files, {
        tableType: "ivf",
        eventId: "",
        propsFiles: files
      });
    },
    sendCellosign() {
      const patientData = {
        passport: this.row.passport,
        firstName: this.row.firstName,
        lastName: this.row.lastName,
        phone: this.row.patient_phone
      };
      this.$modal.show(IVF, { patientData });
    },
    /**
     * Activated by clicking on a row. Opens
     * the event page by id.
     */
    openEventPage() {
      if (!this.row.id) return;
      this.$router.push({
        name: this.eventRouteName,
        params: { id: this.row.id }
      });
    },
    /**
     * @param {String | undefined} type from event.
     * Usually called scheduled_by.
     * @returns {String} text (alt of image).
     */
    getAppScheduledByText(type) {
      const imageObj = this.$store.getters["getAppScheduledBy"](type, false);
      if (imageObj === null) return "-";
      return imageObj.text;
    },
    openWhatsapp(phone) {
      this.$modal.show(Whatsapp, { patientPhone: phone });
    },
    async toggleAvailability() {
      if (this.isUpdatingAvailability) return;

      this.isUpdatingAvailability = true;

      try {
        const currentAvailability = this.row.is_available_online === "זמין";

        await this.$API.updatePathologyAvailability(
          this.row.row_id,
          currentAvailability,
          this.activeDoctor.license,
          this.activeDoctor.id
        );

        this.wasSent = true;
      } catch (error) {
        this.showErrorModal("שגיאה בעדכון הזמינות, אנא נסו שנית");
      } finally {
        this.isUpdatingAvailability = false;
      }
    }
  }
};
</script>

<style scoped>
.pathology-doc-hit {
  cursor: pointer;
  text-align: center;
}
.pathology-doc-hit:focus-visible {
  outline: 2px solid var(--blue, #2d6cdf);
  outline-offset: 2px;
}
/* Pathology table CTAs: same visual language (soft blue) as each other, not .unavailable red / not gray "secondary" */
.doc-table-container.pathology-doc-create,
.doc-table-container.pathology-availability-cta.availability-change-btn:not(.disabled) {
  line-height: normal;
  color: #1a3a5c;
  background-color: #e8f1fc;
  border: 1px solid #b8d4f0;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.doc-table-container.pathology-doc-create:hover,
.doc-table-container.pathology-availability-cta.availability-change-btn:not(.disabled):hover {
  background-color: #d8e8f8;
  border-color: #9ec0e8;
  color: #1a3a5c;
}
.doc-table-container.pathology-doc-create .doc-table-label {
  color: var(--blue-dark, #2c3e50);
}
.doc-table-container.pathology-doc-pending {
  line-height: normal;
  color: #7a8a9a;
  background-color: #f0f2f5;
  border: 1px solid #d5dae0;
  cursor: default;
  pointer-events: none;
  opacity: 0.8;
}
.doc-table-container.pathology-doc-pending .doc-table-label {
  color: #7a8a9a;
}
</style>
